/**
 * RTWO Cryptography & Identity Engine — Provider Factory
 */

import type { IdentityBackendType } from '../adapters/identity-backend-interface';
import type { IKeyProvider, KeyProviderType } from '../adapters/key-provider-interface';
import type { IIdentityBackend } from '../adapters/identity-backend-interface';
import type { IHashProvider } from '../adapters/hash-provider-interface';
import { Ed25519KeyProvider } from './ed25519-key-provider';
import { Secp256k1KeyProvider } from './secp256k1-key-provider';
import { HsmKeyProvider } from './hsm-key-provider';
import { KmsKeyProvider } from './kms-key-provider';
import { SecureEnclaveKeyProvider } from './secure-enclave-key-provider';
import { PeerNetworkIdentityBackend } from './peer-network-identity-backend';
import { SystemRegistryIdentityBackend } from './system-registry-identity-backend';
import { Sha256HashProvider } from './crypto/sha256-hash-provider';

export const OPERATIONAL_KEY_PROVIDER_TYPES: KeyProviderType[] = ['ed25519', 'secp256k1'];

export function isOperationalKeyProvider(providerType: KeyProviderType): boolean {
  return OPERATIONAL_KEY_PROVIDER_TYPES.includes(providerType);
}

export function createKeyProvider(providerType: KeyProviderType): IKeyProvider | null {
  switch (providerType) {
    case 'ed25519':
      return new Ed25519KeyProvider();
    case 'secp256k1':
      return new Secp256k1KeyProvider();
    case 'hsm':
      return new HsmKeyProvider();
    case 'kms':
      return new KmsKeyProvider();
    case 'secure_enclave':
      return new SecureEnclaveKeyProvider();
    default:
      return null;
  }
}

export function createOperationalKeyProviders(): Map<KeyProviderType, IKeyProvider> {
  const providers = new Map<KeyProviderType, IKeyProvider>();
  for (const providerType of OPERATIONAL_KEY_PROVIDER_TYPES) {
    const provider = createKeyProvider(providerType);
    if (provider !== null) {
      providers.set(providerType, provider);
    }
  }
  return providers;
}

export function createScaffoldKeyProviders(): Map<KeyProviderType, IKeyProvider> {
  const providers = new Map<KeyProviderType, IKeyProvider>();
  for (const providerType of ['hsm', 'kms', 'secure_enclave'] as KeyProviderType[]) {
    const provider = createKeyProvider(providerType);
    if (provider !== null) {
      providers.set(providerType, provider);
    }
  }
  return providers;
}

export function createIdentityBackend(backendType: IdentityBackendType): IIdentityBackend | null {
  switch (backendType) {
    case 'system_registry':
      return new SystemRegistryIdentityBackend();
    case 'peer_network':
      return new PeerNetworkIdentityBackend();
    default:
      return null;
  }
}

export function createIdentityBackendCatalog(): Map<IdentityBackendType, IIdentityBackend> {
  const catalog = new Map<IdentityBackendType, IIdentityBackend>();
  catalog.set('system_registry', new SystemRegistryIdentityBackend());
  catalog.set('peer_network', new PeerNetworkIdentityBackend());
  return catalog;
}

let _hashProviderInstance: Sha256HashProvider | null = null;

export function getSha256HashProvider(): IHashProvider {
  if (_hashProviderInstance === null) {
    _hashProviderInstance = new Sha256HashProvider();
  }
  return _hashProviderInstance;
}

export function configureSha256HashProvider(): IHashProvider {
  const provider = getSha256HashProvider();
  provider.configure();
  return provider;
}
